import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { setCookie } from 'app/common/utils/cookie.util';
import { PasswordValidator } from 'app/common/validators/password.validator';
import { UserValidator } from 'app/common/validators/user.validator';
import { setAnalyticsForSignup } from 'app/services/auth/analytics.service';
import { ICreateAccount, ICredentials } from 'app/services/auth/auth.interface';
import { AuthService } from 'app/services/auth/auth.service';
import { SpinnerService } from 'app/services/spinner/spinner.service';
import { ToastService } from 'app/services/toast-wrapper/toast-wrapper.service';
import { MagicService } from 'app/services/wallet/magic/magic.service';
import { WalletconnectService } from 'app/services/wallet/walletconnect/walletconnect.service';
import { iStatus, eStatus, eProviders } from 'app/services/web3/web3.config';
import { Web3Service } from 'app/services/web3/web3.service';
import { Subscription, first } from 'rxjs';
import { paremetersForRedirectUrl } from '../../../services/marketing/marketing-parameters';
import { MarketingGuestService } from 'app/services/marketing/marketing-guest.service';

@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit, OnDestroy {

  helpLink: string = 'https://discord.com/invite/hYRjSfsWXt';
  createUserForm: FormGroup;
  walletSelector: IWalletSelector[] = WALLET_SELECTOR();
  signSubscription: Subscription;
  password: string;
  isPasswordVisible: boolean = false;
  timestamp: number;
  queryParam: { [key: string]: string };
  marketingSettings: any = paremetersForRedirectUrl['default'];


  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private magicService: MagicService,
    private walletconnectService: WalletconnectService,
    private web3Service: Web3Service,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private router: ActivatedRoute,
    private marketingGuestService: MarketingGuestService
  ) {
    this.createUserForm = this.fb.group({
      email: this.fb.control('', [Validators.required, Validators.email]),
      password: this.fb.control('', [Validators.required, PasswordValidator.passwordloginValidator()]),
      username: this.fb.control('', [Validators.required, UserValidator.userloginValidator(), Validators.minLength(4), Validators.maxLength(20)]),
      isReferal: this.fb.control('', []),
      referralCode: this.fb.control('', []),
      // wallet: this.fb.control('', [Validators.required]),
      isWalletconnect: this.fb.control('', [])
    });

    //to cache query param and send to signUp page
    this.router.queryParams.subscribe(res => this.queryParam = res);
    //get data for marketing
    this.marketingGuestService.marketingGuestDRedirectUrlata$.subscribe(data => {
      this.marketingSettings = data;
      console.log(this.marketingSettings)
    });

    //PATCH QUERY PARAM IN FORM GROUP
    this.router.queryParams.pipe(first()).subscribe(params => {
      if (params?.referralCode) {
        this.createUserForm.patchValue({ isReferal: true, referralCode: params.referralCode });
      }
      if (params?.email) {
        this.createUserForm.patchValue({ email: params.email });
      }
    });
  }

  ngOnDestroy(): void {
    this.signSubscription.unsubscribe();
  }

  ngOnInit(): void {
    //callback after user logged and signed
    // this.web3Service.signStatus.next({ status: eStatus.notinit, value: null });
    this.signSubscription = this.web3Service.signStatus$.subscribe((signRes: Map<string, iStatus>) => {
      let status = signRes.get('sign-up');
      switch (status?.status) {
        case eStatus.success:
          let postSignup: ICreateAccount = {
            username: this.createUserForm.get('username').value,
            email: this.createUserForm.get('email').value,
            password: this.createUserForm.get('password').value,
            provider: this.createUserForm.get('isWalletconnect').value ? 'metamask' : 'magic',
            walletAddress: status.value.account,
            signatureHash: status.value.result,
            timestamp: this.timestamp
          };
          let referralCode: string = this.createUserForm.get('referralCode').value ? this.createUserForm.get('referralCode').value : null;
          this.authService.signUp(postSignup, referralCode).pipe(first()).subscribe(response => {
            if (response.status == 'OK') {
              this.authService.createTalone(status.value.account, this.createUserForm.get('referralCode').value).subscribe(_ => {
                let credentials: ICredentials = {
                  username: this.createUserForm.get('username').value,
                  password: this.createUserForm.get('password').value
                }
                setAnalyticsForSignup(postSignup, response, referralCode);
                this.authService.signIn(credentials).pipe(first()).subscribe(response => {
                  if (response.status == "done") {
                    let provider = this.createUserForm.get('isWalletconnect').value ? eProviders.walletconnect : eProviders.magic;
                    setCookie('login', provider, 0.5);
                    setCookie('loginEmail', response.email, 0.5);
                    setCookie('recordedWallet', response.walletAddress /* === eProviders.magic ? 'magic' : 'wallet' */, 0.5);
                    this.authService.routeAfterSuccessfulLogin(response);
                  } else {
                    this.toastService.genericError();
                    console.log('errorLogin', response);
                  }
                });
              })
            } else if (response.status === "err-wallet-used") this.toastService.genericError('errorMsg.walletAlreadyUsed')
            else if (response.status === "err" && response.errorObject.error === "NameNotAvailable") this.toastService.add({
              severity: 'error',
              summary: 'errorMsg.usernameNotAvailable',
              detail: 'msg.pickAnotherUsername',
            });
            else {
              this.spinnerService.setSpinner(false);
              this.toastService.genericError();

              console.log('error signup', response);
            }
          });
          break;
        case eStatus.failed:
          this.toastService.genericError();
          console.log('error', status.value);
          break;
        case eStatus.pending:

          break;

        default:
          break;
      }
    });

    this.createUserForm.get('password').valueChanges.subscribe(
      value => {
        this.password = value;
      }
    );
  }

  createAccount() {
    this.timestamp = new Date().valueOf();
    if (this.createUserForm.get('isWalletconnect').value) this.createWalletconnectAccount();
    else this.createMagicAccount();
  }

  createMagicAccount() {
    this.magicService.loggedSuccessfully.subscribe(logged => {
      if (logged) { this.web3Service.getAccounts().pipe(first()).subscribe(accounts => this.web3Service.signMessage(accounts[0], 'sign-up', 'Generate my starter Kryptomons: ' + this.timestamp)) }
    });
    this.magicService.login(this.createUserForm.get('email').value, false);
  }

  createWalletconnectAccount() {
    this.walletconnectService.loggedSuccessfully.subscribe(logged => {
      if (logged) { this.web3Service.getAccounts().pipe(first()).subscribe(accounts => this.web3Service.signMessage(accounts[0], 'sign-up', 'Generate my starter Kryptomons: ' + this.timestamp)) }
    });
    this.walletconnectService.login(false);
  }

}


const WALLET_SELECTOR = (): IWalletSelector[] => {
  return [
    {
      src: "./assets/img/k-logo.png",
      label: "Create account with KMON wallet",
      value: eProviders.magic
    },
    {
      src: "./assets/img/Icon/wallet-connect.png",
      label: "Create account with your own wallet",
      value: eProviders.walletconnect
    }
  ]
}

interface IWalletSelector {
  src: string
  label: string
  value: eProviders
}
